/**
 * The shape of a campaign artifact at rest, and the rules for putting a list of
 * them together.
 *
 * A campaign produces documents — a narrative, its masked audit, or a refusal
 * record for a run that measured nothing. Those are what the run is graded on, so
 * they outlive the panel that showed them, the campaign that made them, and now
 * the session itself: they go into the session file.
 *
 * Two things govern the record shape.
 *
 * **The text is stored masked.** That is how it arrives from the engine and how
 * `CompletionBlock` holds it; the readable copy is produced at render time. So
 * masked is the original and the decoded copy is derived, never the other way
 * round, and *Download masked audit* keeps writing the same bytes after a round
 * trip that it wrote before one.
 *
 * **Each record carries its own alias bindings.** The live registry decodes using
 * a map keyed by `workspaceFingerprint`, and compound order is part of that
 * fingerprint — so a restore that brings the same compounds back in a different
 * order would silently leave a document full of `COMPOUND_A`. A record that
 * carries the bindings it was written with does not depend on the workspace it
 * lands in, and does not disturb the live registry either.
 *
 * Bindings hold real display names. They are browser-side only, exactly like the
 * alias store's records: never attach them to a wire snapshot, a submission or a
 * decision. Decoding lives on the deputy side — this module stays pure so the
 * gibbs layer keeps its one-way dependency on nothing.
 *
 * @module gibbs/autonomousCampaign/narrative/campaignArtifacts
 */

/** What kind of document a record holds. They are not interchangeable. */
export const CampaignArtifactKind = {
    /** A run that produced a narrative. */
    Narrative: 'narrative',
    /**
     * A run refused before it dispatched anything. It measured nothing, so it
     * carries no decisions, no evidence and no ranked leads — rule 10.
     */
    Refusal: 'refusal',
};

/**
 * Newest-first, and bounded. A session that runs campaigns all day should not
 * grow a session file without limit; the oldest documents are the ones a reviewer
 * has already dealt with.
 */
export const CAMPAIGN_ARTIFACT_LIMIT = 50;

function trimmedString(value) {
    return typeof value === 'string' ? value : '';
}

function plainObject(value) {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

/**
 * Copy a masked-id -> entry map, entries included.
 *
 * Entry-deep, not just top-level: spreading only the outer object leaves every
 * entry a shared reference, so a caller still holding the live map could change a
 * display name inside a document that was supposed to be finished. Entries are
 * plain data (`{maskedId, displayName, localIdentityKey?, flags}`), so one level
 * deeper is the whole of it.
 */
function copyBindingMap(map) {
    const source = plainObject(map);
    return Object.keys(source).reduce((out, maskedId) => {
        const entry = source[maskedId];
        out[maskedId] = entry && typeof entry === 'object' && !Array.isArray(entry)
            ? { ...entry }
            : entry;
        return out;
    }, {});
}

/**
 * The alias bindings a record needs to decode itself, in the shape the masking
 * formatters read (`{ aliases, proteinAliases }`).
 *
 * Copied rather than referenced: the live registry replaces its snapshot whenever
 * the workspace changes, and a record that pointed at it would decode against
 * whatever the workspace looks like later.
 *
 * @param {{aliases?: object, proteinAliases?: object}} [snapshot] a registry snapshot
 */
export function campaignArtifactBindings(snapshot) {
    const source = plainObject(snapshot);
    return {
        aliases: copyBindingMap(source.aliases),
        proteinAliases: copyBindingMap(source.proteinAliases),
    };
}

/**
 * Normalize one artifact into the record that is stored and serialized.
 *
 * @param {object} input
 * @param {string} input.id stable identity; a repeat of the same id is the same document
 * @param {string} [input.kind] see `CampaignArtifactKind`
 * @param {string} [input.status] terminal campaign status, or the refusal stage
 * @param {string} [input.label] what the list calls it
 * @param {string} [input.narrative] the masked document text
 * @param {object} [input.aliasBindings] bindings this document decodes with
 * @param {number} [input.createdAt] epoch ms; the caller owns the clock
 * @param {boolean} [input.restoredFromFile] came out of a session file, not a live run
 * @returns {object|null} null when there is no document to store
 */
export function campaignArtifactRecord({
    id,
    kind = CampaignArtifactKind.Narrative,
    status = '',
    label = '',
    narrative = '',
    aliasBindings = null,
    createdAt = 0,
    restoredFromFile = false,
} = {}) {
    const text = trimmedString(narrative);
    // No document, nothing to keep. An empty terminal frame is a real case —
    // a cancelled run reports a status and no narrative — and it must not become
    // an entry offering an empty download.
    if (!trimmedString(id) || !text.trim()) return null;
    return {
        id,
        kind: Object.values(CampaignArtifactKind).includes(kind)
            ? kind
            : CampaignArtifactKind.Narrative,
        status: trimmedString(status),
        label: trimmedString(label),
        narrative: text,
        aliasBindings: campaignArtifactBindings(aliasBindings),
        createdAt: Number.isFinite(createdAt) ? createdAt : 0,
        restoredFromFile: restoredFromFile === true,
    };
}

/**
 * Add records to a list, newest first, without duplicating a document already
 * held and without growing past the limit.
 *
 * First write wins on a repeated id. A terminal frame can be replayed by the
 * Orchestrator on any reconnect, and the copy already held is the one whose label
 * and bindings match the run as it was seen.
 *
 * @param {object[]} existing
 * @param {object[]} incoming
 * @returns {object[]} the same array reference when nothing was added
 */
export function mergeCampaignArtifacts(existing = [], incoming = []) {
    const held = Array.isArray(existing) ? existing : [];
    const seen = new Set(held.map((entry) => entry?.id));
    const added = [];
    for (const candidate of Array.isArray(incoming) ? incoming : []) {
        const record = candidate?.narrative !== undefined && candidate?.aliasBindings !== undefined
            ? candidate
            : campaignArtifactRecord(candidate);
        if (!record || seen.has(record.id)) continue;
        seen.add(record.id);
        added.push(record);
    }
    if (added.length === 0) return held;
    return [...added, ...held].slice(0, CAMPAIGN_ARTIFACT_LIMIT);
}

/**
 * The artifacts as they go into a session file.
 *
 * `restoredFromFile` is deliberately not serialized: it describes where a record
 * came from in *this* session, and everything read back out of a file is restored
 * by definition.
 *
 * @param {object[]} artifacts
 * @returns {object[]}
 */
export function serializeCampaignArtifacts(artifacts = []) {
    return (Array.isArray(artifacts) ? artifacts : [])
        .map((entry) => campaignArtifactRecord(entry))
        .filter(Boolean)
        .map(({ restoredFromFile: _dropped, ...record }) => record);
}

/**
 * The artifacts as they come out of a session file.
 *
 * Every record is marked restored, so nothing read out of a file can present
 * itself as a run that just happened.
 *
 * @param {unknown} raw the `CampaignArtifacts` field, from a file of any age
 * @returns {object[]}
 */
export function deserializeCampaignArtifacts(raw) {
    if (!Array.isArray(raw)) return [];
    return raw
        .map((entry) => campaignArtifactRecord({ ...plainObject(entry), restoredFromFile: true }))
        .filter(Boolean)
        .slice(0, CAMPAIGN_ARTIFACT_LIMIT);
}

/**
 * How a terminal campaign status reads to a person.
 *
 * One home for the mapping, because printing the engine's own status word put
 * "Campaign escalated" on a run a human had simply stopped. `escalated` is the
 * engine's term for a checkpoint nobody carried forward; from the reviewer's
 * side that is a stop, and the step-card path has always said so.
 *
 * Returns null for a status this does not recognize, so each caller keeps the
 * fallback it already had rather than inheriting one.
 *
 * @param {string} status - terminal campaign status
 * @returns {string|null}
 */
export function campaignStatusLabel(status) {
    if (status === 'cancelled') return 'Cancelled';
    if (
        status === 'terminated'
        || status === 'stopped'
        || status === 'escalated'
        || status === 'budget-exceeded'
    ) return 'Stopped';
    if (status === 'error' || status === 'failed') return 'Error';
    if (status === 'complete' || status === 'completed') return 'Complete';
    return null;
}

/**
 * How the list labels a record. A restored document says so on its face: it is a
 * historical artifact, not a result this session produced.
 *
 * @param {object} artifact
 * @returns {string}
 */
export function campaignArtifactLabel(artifact) {
    const status = trimmedString(artifact?.status);
    const statusWord = campaignStatusLabel(status)?.toLowerCase() || status || 'complete';
    const label = trimmedString(artifact?.label)
        || (artifact?.kind === CampaignArtifactKind.Refusal
            ? 'Campaign refused'
            : `Campaign ${statusWord}`);
    return artifact?.restoredFromFile ? `${label} — restored from a session file` : label;
}
